import React, { ReactNode } from 'react'

import { useTheme } from 'next-themes'
import Image from 'next/image'
import { twMerge } from 'tailwind-merge'

import FreeTrialBanner from '@/components/billing/FreeTrialBanner'
import { ServerBanner } from '@/components/common/Banner'
import { CollapsibleSidebar } from '@/components/common/Sidebar/CollapsibleSidebar'
import { LegacySidebar } from '@/components/common/Sidebar/LegacySidebar'

import { useIsTrialPeriod } from '@/hooks/billing/useIsTrialPeriod'
import { useBanner } from '@/hooks/common/useBanner'
import { useMounted } from '@/hooks/common/useMounted'

import { useSidebarStore, useSidebarVariant } from '@/stores/SidebarStore'

interface LayoutProps {
  navbar?: React.ComponentType
  children: ReactNode
  className?: string
  contentClassName?: string
  innerClassName?: string
  showUpgrade?: boolean
}

interface LayoutRootProps {
  children: ReactNode
  className?: string
}

export const LayoutRoot: React.FC<LayoutRootProps> = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-col h-screen w-screen overflow-hidden',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface LayoutBannersProps {
  showUpgrade?: boolean
}

export const LayoutBanners: React.FC<LayoutBannersProps> = ({
  showUpgrade = true,
}) => {
  const { trialExpired, isTrialPeriod, daysRemaining } = useIsTrialPeriod()
  const { data: banner } = useBanner()

  const showTrialBanner = (isTrialPeriod || trialExpired) && showUpgrade
  const showServerBanner = banner && banner.show

  return (
    <div className="flex shrink w-full overflow-hidden relative bg-primary-50 dark:bg-default-dark">
      {showTrialBanner && !showServerBanner && (
        <div className="w-full">
          <FreeTrialBanner daysRemaining={daysRemaining} />
        </div>
      )}
      <ServerBanner />
    </div>
  )
}

interface LayoutContentProps {
  children: ReactNode
  className?: string
}

export const LayoutContent: React.FC<LayoutContentProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-primary-50 dark:bg-default-dark',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const LayoutAmericaMode: React.FC = () => {
  const { resolvedTheme } = useTheme()
  const isMounted = useMounted()

  if (!isMounted || resolvedTheme !== 'america') {
    return null
  }

  return (
    // TODO: this image seems to work with SSR, need to investigate
    <div className="absolute -top-16 -left-2 w-full pointer-events-none overflow-hidden">
      <Image
        src="/flag.gif"
        alt="flag"
        className="hidden md:block opacity-[6%] pointer-events-none"
        width={340}
        height={250}
      />
    </div>
  )
}

interface LayoutSidebarProps {
  className?: string
}

export const LayoutSidebar: React.FC<LayoutSidebarProps> = ({ className }) => {
  const variant = useSidebarVariant()
  const { expanded } = useSidebarStore()

  if (variant === 'legacy') {
    return (
      <div
        className={twMerge(
          'hidden md:block w-0 relative z-[41]',
          expanded
            ? 'md:w-[226px] border-r border-primary-300 dark:border-primary-800'
            : 'md:w-[56px]',
          className,
        )}
      >
        <LegacySidebar expanded={expanded} />
      </div>
    )
  }

  return (
    <div
      className={twMerge(
        'hidden md:flex flex-shrink-0 relative z-[9999] overflow-visible',
        className,
      )}
      style={{ width: 64 }} // Matches SIDEBAR_WIDTH in CollapsibleSidebar
    >
      <CollapsibleSidebar />
    </div>
  )
}

interface LayoutMainContentProps {
  children: ReactNode
  className?: string
}

export const LayoutMainContent: React.FC<LayoutMainContentProps> = ({
  children,
  className,
}) => {
  const variant = useSidebarVariant()
  const { expanded } = useSidebarStore()

  // Legacy sidebar uses class-based widths
  if (variant === 'legacy') {
    return (
      <div
        className={twMerge(
          'flex min-h-0 flex-1 flex-col w-full',
          expanded ? 'md:w-[calc(100%-226px)]' : 'md:w-[calc(100%-56px)]',
          className,
        )}
      >
        {children}
      </div>
    )
  }

  // Standard sidebar uses class-based width to avoid SSR hydration mismatch
  return (
    <div
      className={twMerge(
        'flex min-h-0 flex-1 flex-col w-full md:w-[calc(100%-64px)]', // 64px matches SIDEBAR_WIDTH in CollapsibleSidebar
        className,
      )}
    >
      {children}
    </div>
  )
}

interface LayoutInnerContentProps {
  children: ReactNode
  className?: string
  noNavbar?: boolean
}

export const LayoutInnerContent: React.FC<LayoutInnerContentProps> = ({
  children,
  className,
  noNavbar = false,
}) => {
  const variant = useSidebarVariant()
  const { expanded } = useSidebarStore()

  // Legacy sidebar has special border styling when collapsed
  const legacyCollapsedStyles =
    variant === 'legacy' && !expanded ? 'border-l' : ''
  const legacyCollapsedWithNavbarStyles =
    variant === 'legacy' && !expanded ? 'border-l md:rounded-tl-lg' : ''

  if (noNavbar) {
    return (
      <div
        className={twMerge(
          'flex min-h-0 flex-1 overflow-auto border-primary-300 dark:border-primary-800 bg-white dark:bg-default-dark',
          legacyCollapsedStyles,
          className,
        )}
      >
        {children}
      </div>
    )
  }
  return (
    <div
      className={twMerge(
        'flex min-h-0 flex-1 overflow-auto border-t border-primary-300 dark:border-primary-800 bg-white dark:bg-default-dark',
        legacyCollapsedWithNavbarStyles,
        className,
      )}
    >
      {children}
    </div>
  )
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  navbar: Navbar,
  className = '',
  contentClassName = '',
  innerClassName = '',
  showUpgrade = true,
}) => {
  return (
    <LayoutRoot className={className}>
      <LayoutBanners showUpgrade={showUpgrade} />
      <LayoutContent className={contentClassName}>
        <LayoutAmericaMode />
        {/* wrapping in relative allows the content to stack on top of the document */}
        <div className="relative flex min-h-0 flex-1 w-full">
          <LayoutSidebar />
          <LayoutMainContent>
            {Navbar && (
              <div className="flex-shrink-0 sticky top-0 w-full z-40">
                <Navbar />
              </div>
            )}
            <LayoutInnerContent
              className={innerClassName}
              noNavbar={Navbar === undefined}
            >
              {children}
            </LayoutInnerContent>
          </LayoutMainContent>
        </div>
      </LayoutContent>
    </LayoutRoot>
  )
}

export default Layout
